import { useEffect, useRef } from 'react';
import { useNarrationMode } from './useNarrationMode';
import type { useTtsQueue } from './useTtsQueue';
import type { UseVisitedParagraphsResult } from './useVisitedParagraphs';

/**
 * V4 Voice GM (2026-06-02) — auto-narrate the current view.
 *
 * Watches the view key (briefing → paragraph N → paragraph M → ending)
 * and enqueues TTS via the supplied queue. In 'full' mode the full
 * paragraph text plays; in 'brief' mode only a short cue.
 *
 * V6.2.A (2026-06-02): added 'first-visit' mode. On the first encounter
 * of a view key (per `visited.isVisited`) the full text plays; on
 * revisits the brief cue plays. The hook marks the key visited AFTER
 * the enqueue so the SAME render doesn't see itself as revisited.
 *
 * Important sequencing — when the dispatcher just spoke a rationale via
 * the same queue, the paragraph autoplay LANDS BEHIND the rationale in
 * the queue, giving the V4 Q4 user pick (sequential rationale →
 * paragraph) for free.
 *
 * Designed as a coordinator hook (no service / mutation side-effects
 * beyond queue.enqueue + visited.markVisited), so consuming components
 * stay simple.
 */

export interface AutoNarrateViewSnapshot {
  /** Identity-stable key derived from the view (e.g. 'briefing' or 'p:57'). */
  key: string;
  /** Text the queue should narrate in 'full' mode. */
  text: string;
  /** Text the queue should narrate in 'brief' mode. */
  briefText: string;
}

export function useAutoNarrate(
  view: AutoNarrateViewSnapshot,
  ttsQueue: ReturnType<typeof useTtsQueue>,
  enabled: boolean,
  visited?: UseVisitedParagraphsResult,
) {
  const [mode] = useNarrationMode();
  const lastKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (view.key === lastKeyRef.current) {
      return;
    }
    lastKeyRef.current = view.key;
    let effectiveMode: 'full' | 'brief' = mode === 'brief' ? 'brief' : 'full';
    if (mode === 'first-visit') {
      effectiveMode = visited && visited.isVisited(view.key) ? 'brief' : 'full';
    }
    const text = effectiveMode === 'brief' ? view.briefText : view.text;
    if (text.trim().length === 0) {
      return;
    }
    ttsQueue.enqueue(text, { source: `autonarrate:${view.key}` });
    if (mode === 'first-visit' && visited) {
      visited.markVisited(view.key);
    }
    // mode + visited are intentionally observed inside the effect via the
    // closure captured at this view-change moment; we DON'T re-fire when
    // the user flips the mode mid-narration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.key, enabled]);
}
